import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, FileText, Package, Users, BarChart3, Receipt, Wallet,
  Globe, Shield, Zap, CheckCircle2, Building2, Book, BookOpen,
  Layers, Boxes, Calendar, PieChart, ScrollText, ChevronRight,
} from "lucide-react";
import SiteHeader, { SerpLogo } from "@/components/SiteHeader";
import FinancialCommandCenter from "@/components/FinancialCommandCenter";

const HERO_STATS = [
  { k: "Countries", v: "10+", tag: "India · UAE · EU · UK · US" },
  { k: "Tax regimes", v: "6", tag: "GST · VAT · Sales Tax" },
  { k: "Currencies", v: "50+", tag: "with live FX" },
  { k: "Voucher types", v: "8", tag: "Sales · Purchase · Journal…" },
];

const FEATURES = [
  { icon: BookOpen, tag: "Accounting", title: "Double-entry ledger",
    body: "Every voucher posts as a balanced debit + credit against your Chart of Accounts — the way real accountants keep books." },
  { icon: ScrollText, tag: "Vouchers", title: "Full voucher suite",
    body: "Sales, Purchase, Payment, Receipt, Contra, Journal, Debit Note and Credit Note — all first-class in the workflow." },
  { icon: PieChart, tag: "Reports", title: "Statutory financials",
    body: "Trial Balance, P&L, Balance Sheet, Cash Flow and Day Book — audit-ready, exportable, drillable to source vouchers." },
  { icon: Boxes, tag: "Inventory", title: "Multi-godown stock",
    body: "Track stock across godowns, groups and categories with UoM conversions, batch numbers and expiry dates." },
  { icon: Globe, tag: "Global", title: "One app, six regimes",
    body: "Choose a country and SERP switches the tax engine, currency, invoice template and voucher formats automatically." },
  { icon: Shield, tag: "Audit", title: "Immutable audit trail",
    body: "Every create / edit / delete on a voucher is logged with user, IP and diff — CA-ready when the auditor knocks." },
];

const REGIONS = [
  { code: "IN", flag: "🇮🇳", name: "India", regime: "GST 5/12/18/28%", currency: "INR" },
  { code: "AE", flag: "🇦🇪", name: "UAE", regime: "VAT 5%", currency: "AED" },
  { code: "GB", flag: "🇬🇧", name: "United Kingdom", regime: "VAT 20%", currency: "GBP" },
  { code: "DE", flag: "🇩🇪", name: "Germany", regime: "VAT 19%", currency: "EUR" },
  { code: "FR", flag: "🇫🇷", name: "France", regime: "VAT 20%", currency: "EUR" },
  { code: "US", flag: "🇺🇸", name: "United States", regime: "State sales tax", currency: "USD" },
];

const PLANS = [
  { name: "Starter", tag: "Try SERP forever",
    features: ["1 company", "2 users", "Unlimited invoices", "Basic reports"],
    cta: "Get started", href: "/register", highlight: false },
  { name: "Business", tag: "For growing SMEs",
    features: ["5 companies", "10 users", "Full voucher suite", "Multi-godown inventory",
                 "GSTR-1, GSTR-3B, VAT return", "Priority support"],
    cta: "Start 14-day trial", href: "/register", highlight: true },
  { name: "Enterprise", tag: "For groups & multi-entity",
    features: ["Unlimited companies", "SSO + role audit", "Custom voucher types",
                 "Dedicated success mgr", "API + webhooks"],
    cta: "Talk to us", href: "/contact", highlight: false },
];

const RESOURCES = [
  { icon: BookOpen, title: "SERP for CAs",
    body: "One login, every client's books. See how CA firms move from Tally to SERP in a weekend." },
  { icon: Book, title: "GST Handbook 2026",
    body: "Every rate, every filing, every deadline — bookmarked so you never miss a return." },
  { icon: Calendar, title: "Onboarding Playbook",
    body: "A 5-day checklist to migrate parties, items and opening balances from Tally / Excel." },
];

function Section({ id, children, className = "" }) {
  return <section id={id} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 ${className}`}>{children}</section>;
}

export default function Landing() {
  return (
    <div style={{ background: "#F8FAFC" }} className="min-h-screen">
      <SiteHeader />

      {/* ─── HERO / ABOUT ─── */}
      <section id="about"
        className="relative overflow-hidden bg-white"
      >
        <Section className="relative py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono-data uppercase tracking-widest border border-[#FF5500]/30 bg-[#FFEDE0] text-[#FF5500] mb-6" data-testid="landing-hero-badge">
                <Zap size={12}/> Global ERP · Tally-grade accounting
              </div>
              <h1 className="font-heading font-bold tracking-tight leading-[1.05] text-[#1C1C1C]" style={{ fontSize: "clamp(1.85rem, 5vw, 4.2rem)" }}>
                Enterprise accounting.<br/>
                <span className="text-[#FF5500]">Global by default.</span><br/>
                <span className="text-[#4B5563]" style={{ fontSize: "0.8em" }}>Local where it counts.</span>
              </h1>
              <p className="text-lg text-[#4B5563] mt-6 max-w-xl leading-relaxed">
                SERP is a full double-entry ERP & accounting suite for multi-country SMEs and CAs —
                Sales, Purchase, Payment, Receipt, Journal vouchers plus Balance Sheet, P&L and Trial Balance,
                localized for India (GST), UAE (VAT), Europe, UK and US.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/register" data-testid="landing-cta-primary"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md font-semibold text-sm bg-[#FF5500] hover:bg-[#E64A00] text-white transition"
                >
                  Create Account <ArrowRight size={16}/>
                </Link>
                <Link to="/login" data-testid="landing-cta-signin"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md font-semibold text-sm border border-[#1C1C1C] hover:bg-[#F5F5F5] text-[#1C1C1C] transition"
                >
                  Login
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-6 text-xs text-[#6B7280] flex-wrap">
                <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#FF5500]"/> No credit card</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#FF5500]"/> Audit-ready</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#FF5500]"/> Approved onboarding</span>
              </div>
            </div>

            <div className="lg:col-span-5" data-testid="landing-hero-preview">
              <FinancialCommandCenter />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-14" data-testid="landing-stats">
            {HERO_STATS.map((s) => (
              <div key={s.k} className="rounded-md px-5 py-4 border bg-white" style={{ borderColor: "#E5E7EB" }}>
                <div className="text-2xl font-bold font-mono-data text-[#FF5500]">{s.v}</div>
                <div className="text-xs text-[#4B5563] mt-1">{s.k}</div>
                <div className="text-[10px] font-mono-data text-[#9CA3AF] mt-1 tracking-wider uppercase">{s.tag}</div>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* ─── PRODUCT ─── */}
      <Section id="product" className="py-24 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="overline mb-2">The product</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] mb-4">
              One suite, three layers.
            </h2>
            <p className="text-[#475569] leading-relaxed">
              SERP is built like the accounting textbook: a rock-solid double-entry ledger at the foundation,
              a voucher workflow that mirrors how businesses actually operate on top, and
              country-specific tax & compliance modules on the outside.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                ["Foundation", "Chart of accounts, ledgers, groups, opening balances, fiscal years."],
                ["Vouchers",   "Sales, Purchase, Payment, Receipt, Contra, Journal, Debit & Credit Notes."],
                ["Compliance", "GSTR-1 / 3B, VAT returns, e-invoicing (India), e-way bill."],
              ].map(([t, b]) => (
                <li key={t} className="flex gap-3">
                  <div className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: "#1E3A8A" }}/>
                  <div><b className="text-[#0F172A]">{t}.</b> <span className="text-sm text-[#475569]">{b}</span></div>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="card-surface p-5">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: BookOpen, label: "Ledgers & Groups", color: "#1E3A8A" },
                  { icon: ScrollText, label: "8 Voucher types", color: "#3B82F6" },
                  { icon: Boxes, label: "Multi-godown inventory", color: "#0EA5E9" },
                  { icon: Users, label: "Parties · AR / AP",   color: "#16A34A" },
                  { icon: PieChart, label: "Financial statements", color: "#F59E0B" },
                  { icon: Shield, label: "Audit trail",         color: "#DC2626" },
                ].map((c) => (
                  <div key={c.label} className="rounded-md p-4 border border-[#E2E8F0]">
                    <div className="w-9 h-9 rounded-md flex items-center justify-center mb-3"
                      style={{ background: `${c.color}22` }}>
                      <c.icon size={16} style={{ color: c.color }}/>
                    </div>
                    <div className="font-semibold text-sm text-[#0F172A]">{c.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ─── FEATURES ─── */}
      <section id="features" className="py-24 lg:py-28" style={{ background: "#F1F5F9" }}>
        <Section>
          <div className="text-center mb-14">
            <div className="overline mb-2">Everything an ERP needs</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A]">Tally-grade features, modern experience.</h2>
            <p className="text-[#475569] max-w-2xl mx-auto mt-3 text-base">
              Every module is designed for the day-to-day of a real Indian SME or a global CA firm.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="landing-features">
            {FEATURES.map((f) => (
              <div key={f.title} className="card-surface p-5 hover:border-[#1E3A8A] transition-all">
                <div className="w-10 h-10 rounded-md flex items-center justify-center mb-4"
                  style={{ background: "#E0E7FF" }}>
                  <f.icon size={18} className="text-[#1E3A8A]"/>
                </div>
                <div className="overline mb-1">{f.tag}</div>
                <div className="text-lg font-semibold text-[#0F172A] mb-2">{f.title}</div>
                <div className="text-sm text-[#475569] leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <Section id="how" className="py-24 lg:py-28">
        <div className="text-center mb-14">
          <div className="overline mb-2">Get running in 5 minutes</div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A]">Three steps to your first voucher.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="landing-how-it-works">
          {[
            { n: "1", icon: Building2, t: "Create your workspace",
              b: "Sign up, pick your country and tax regime. SERP prepares your Chart of Accounts and default voucher types." },
            { n: "2", icon: Users, t: "Add ledgers & parties",
              b: "Import Tally ledgers from CSV or start fresh. Customers, vendors, banks — grouped under the standard 28 groups." },
            { n: "3", icon: ScrollText, t: "Post your first voucher",
              b: "Sales, Purchase, Payment, Receipt or Journal. Everything double-entry balanced. Reports update instantly." },
          ].map((s) => (
            <div key={s.n} className="relative card-surface p-6">
              <div className="absolute -top-4 left-6 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                style={{ background: "#1E3A8A" }}>{s.n}</div>
              <s.icon size={20} className="text-[#1E3A8A] mt-3 mb-3"/>
              <div className="text-lg font-semibold mb-2">{s.t}</div>
              <div className="text-sm text-[#475569] leading-relaxed">{s.b}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── WORKFLOW ─── */}
      <section id="workflow" className="py-24 lg:py-28 bg-white border-y" style={{ borderColor: "#E5E7EB" }}>
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="overline mb-2" style={{ color: "#FF5500" }}>The daily workflow</div>
              <h2 className="text-3xl font-bold leading-tight text-[#1C1C1C]">Voucher → Ledger → Statement.</h2>
              <p className="text-[#4B5563] mt-4 leading-relaxed">
                A single sale creates 3 side-effects — inventory reduces, the customer ledger receives a debit,
                and the sales ledger receives a credit. SERP does all three atomically so your Trial Balance is
                <b> always balanced</b>, without you thinking about it.
              </p>
              <div className="mt-6 text-xs font-mono-data text-[#6B7280]">
                Dr. Sundry Debtors 1,18,000 &nbsp;·&nbsp; Cr. Sales 1,00,000 &nbsp;·&nbsp; Cr. Output CGST 9,000 &nbsp;·&nbsp; Cr. Output SGST 9,000
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { icon: ScrollText, name: "Sales Voucher", d: "Creates AR + Sales + Tax posts" },
                  { icon: Package, name: "Stock impact",    d: "Reduces item qty from godown" },
                  { icon: PieChart, name: "P&L updates",     d: "Revenue line auto-refreshes" },
                  { icon: Wallet, name: "Receipt Voucher",  d: "Clears AR + records into Bank" },
                  { icon: BookOpen, name: "Ledger view",     d: "Every voucher drill-through" },
                  { icon: Shield, name: "Audit log",        d: "Every edit tracked with diff" },
                ].map((c, i) => (
                  <div key={c.name} className="rounded-md p-4 border bg-white" style={{ borderColor: "#E5E7EB" }}>
                    <div className="flex items-center justify-between mb-2">
                      <c.icon size={16} className="text-[#FF5500]"/>
                      <span className="text-[10px] font-mono-data text-[#9CA3AF]">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <div className="text-[#1C1C1C] font-semibold text-sm">{c.name}</div>
                    <div className="text-[11px] text-[#6B7280] mt-1">{c.d}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>
      </section>

      {/* ─── PLANS ─── */}
      <Section id="pricing" className="py-24 lg:py-28">
        <div className="text-center mb-14">
          <div className="overline mb-2">Plans</div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A]">Simple plans for every stage.</h2>
          <p className="text-[#475569] max-w-2xl mx-auto mt-3 text-base">Every plan includes double-entry accounting, statutory reports and full audit trail. Talk to us for tailored pricing.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="landing-pricing">
          {PLANS.map((p) => (
            <div key={p.name}
              className={`p-6 rounded-xl border ${p.highlight ? "border-[#1E3A8A] shadow-xl" : "border-[#E2E8F0]"} bg-white relative`}
              data-testid={`plan-${p.name.toLowerCase()}`}
              style={p.highlight ? { transform: "translateY(-6px)" } : {}}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-[#1E3A8A] text-white text-[10px] font-bold uppercase tracking-widest">
                  Most Popular
                </div>
              )}
              <div className="text-sm text-[#64748B] mb-1">{p.tag}</div>
              <div className="text-2xl font-bold text-[#0F172A] mb-6">{p.name}</div>
              <ul className="space-y-2 mb-6">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 text-sm text-[#475569]">
                    <CheckCircle2 size={14} className="text-[#1E3A8A] shrink-0 mt-0.5"/>
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to={p.href}
                data-testid={`plan-cta-${p.name.toLowerCase()}`}
                className={`w-full inline-flex items-center justify-center h-10 rounded-md text-sm font-semibold ${p.highlight ? "bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" : "border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#E0E7FF]"}`}
              >
                {p.cta} <ChevronRight size={14}/>
              </Link>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── RESOURCES ─── */}
      <section id="resources" className="py-24 lg:py-28" style={{ background: "#F1F5F9" }}>
        <Section>
          <div className="text-center mb-14">
            <div className="overline mb-2">Resources</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A]">Learn, migrate, master.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="landing-resources">
            {RESOURCES.map((r) => (
              <div key={r.title} className="card-surface p-6 hover:border-[#1E3A8A]">
                <r.icon size={22} className="text-[#1E3A8A] mb-3"/>
                <div className="text-lg font-semibold text-[#0F172A] mb-2">{r.title}</div>
                <div className="text-sm text-[#475569] leading-relaxed mb-4">{r.body}</div>
                <Link to="/contact" className="text-sm text-[#1E3A8A] font-semibold inline-flex items-center gap-1">
                  Learn more <ChevronRight size={14}/>
                </Link>
              </div>
            ))}
          </div>

          {/* Region strip inside Resources */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-6 gap-3">
            {REGIONS.map((r) => (
              <div key={r.code} className="rounded-md p-3 border border-[#E2E8F0] bg-white text-center">
                <div className="text-2xl mb-1">{r.flag}</div>
                <div className="text-xs font-semibold text-[#0F172A]">{r.name}</div>
                <div className="text-[10px] text-[#64748B] font-mono-data">{r.regime}</div>
                <div className="text-[10px] font-mono-data text-[#1E3A8A] mt-1">{r.currency}</div>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-24 text-center bg-[#FFEDE0]">
        <Section>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono-data uppercase tracking-widest border border-[#FF5500]/30 bg-white text-[#FF5500] mb-6">
            <Shield size={12}/> Approved sign-ups only
          </div>
          <h2 className="text-[#1C1C1C] text-3xl lg:text-5xl font-bold leading-tight mb-4">
            Ready to run your books<br/>the modern way?
          </h2>
          <p className="text-[#4B5563] max-w-2xl mx-auto text-base mb-8">
            Every SERP account is approved manually to keep your workspace secure. It takes minutes — start today and be posting vouchers tomorrow.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/register" data-testid="landing-cta-bottom-primary"
              className="inline-flex items-center gap-2 h-12 px-8 rounded-md font-semibold text-sm bg-[#FF5500] hover:bg-[#E64A00] text-white transition"
            >
              Create Account <ArrowRight size={16}/>
            </Link>
            <Link to="/login" data-testid="landing-cta-bottom-signin"
              className="inline-flex items-center gap-2 h-12 px-8 rounded-md font-semibold text-sm border border-[#1C1C1C] hover:bg-white text-[#1C1C1C] transition"
            >
              Login
            </Link>
          </div>
        </Section>
      </section>

      {/* ─── FOOTER ─── clean light enterprise footer */}
      <footer
        className="relative border-t"
        style={{ background: "#F7F8FB", borderColor: "#E2E8F0", color: "#0F172A" }}
        data-testid="landing-footer"
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-20 pb-8">
          {/* ── CTA row ── */}
          <div className="pb-14 mb-14 border-b grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
                style={{ borderColor: "#E2E8F0" }}>
            <div className="md:col-span-8">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5500] font-semibold mb-3">Get started with SERP</div>
              <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-[#0F172A] leading-tight tracking-tight">
                Run every ledger, voucher and invoice on one <span className="text-[#FF5500]">audit-ready platform.</span>
              </h2>
              <p className="text-sm text-[#475569] mt-4 max-w-2xl leading-relaxed">
                Global by default. Local where it counts. Book a walkthrough with a SERP specialist and see your books in real-time.
              </p>
            </div>
            <div className="md:col-span-4 flex flex-col sm:flex-row md:flex-col gap-3">
              <Link
                to="/register"
                data-testid="footer-cta-register"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold text-white transition-all"
                style={{ background: "#FF5500", boxShadow: "0 8px 20px rgba(255,85,0,0.25)" }}
              >
                Create free account <ArrowRight size={14}/>
              </Link>
              <Link
                to="/contact"
                data-testid="footer-cta-contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold border transition-colors bg-white hover:border-[#FF5500] hover:text-[#FF5500]"
                style={{ borderColor: "#CBD5E1", color: "#0F172A" }}
              >
                Talk to sales <ChevronRight size={14}/>
              </Link>
            </div>
          </div>

          {/* ── Main link grid ── */}
          <div className="grid grid-cols-2 md:grid-cols-12 gap-8">
            {/* Brand column */}
            <div className="col-span-2 md:col-span-4 flex flex-col gap-5">
              <SerpLogo height={34} testid="footer-logo"/>
              <p className="text-sm text-[#475569] leading-relaxed max-w-xs">
                Global Enterprise Resource Planning — double-entry, multi-country, statutory-compliant, audit-ready.
              </p>
              {/* Social */}
              <div className="flex items-center gap-2 pt-1" data-testid="footer-social">
                {[
                  { label: "LinkedIn", href: "https://linkedin.com/company/shanvi-resources", d: "M20.5 2h-17A1.5 1.5 0 0 0 2 3.5v17A1.5 1.5 0 0 0 3.5 22h17a1.5 1.5 0 0 0 1.5-1.5v-17A1.5 1.5 0 0 0 20.5 2zM8 19H5v-9h3v9zM6.5 8.25A1.75 1.75 0 1 1 8.3 6.5a1.78 1.78 0 0 1-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0 0 13 14.19a.66.66 0 0 0 0 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 0 1 2.7-1.4c1.55 0 3.36.86 3.36 3.66V19z" },
                  { label: "X",        href: "https://x.com/shanviresources",    d: "M18.244 2H21.5l-7.11 8.126L23 22h-6.828l-5.34-6.988L4.72 22H1.464l7.626-8.72L1 2h6.98l4.827 6.4L18.244 2zm-1.2 18h1.87L7.05 4H5.05L17.044 20z" },
                  { label: "YouTube",  href: "https://youtube.com/@shanviresources", d: "M23.5 6.5s-.23-1.63-.94-2.34c-.9-.94-1.9-.95-2.36-1C16.9 3 12 3 12 3h-.01s-4.9 0-8.2.16c-.46.05-1.46.06-2.36 1C.72 4.87.5 6.5.5 6.5S.26 8.42.26 10.34v1.8C.26 14.06.5 15.98.5 15.98s.22 1.63.93 2.34c.9.94 2.08.91 2.6 1.01 1.88.17 8 .17 8 .17s4.9-.01 8.2-.17c.46-.05 1.46-.07 2.36-1 .71-.71.94-2.34.94-2.34s.24-1.92.24-3.84v-1.8c0-1.92-.24-3.85-.24-3.85zM9.75 14.34V7.66L15.86 11 9.75 14.34z" },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href} target="_blank" rel="noreferrer noopener"
                    aria-label={s.label}
                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border transition-all hover:border-[#FF5500] hover:scale-105 bg-white"
                    style={{ borderColor: "#E2E8F0" }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="#64748B">
                      <path d={s.d}/>
                    </svg>
                  </a>
                ))}
              </div>
            </div>

            {/* Product */}
            <div className="col-span-1 md:col-span-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5500] font-semibold mb-4">Product</div>
              <ul className="space-y-2 text-sm text-[#475569]">
                <li><a href="#features" className="hover:text-[#0F172A] transition-colors">Ledgers &amp; Vouchers</a></li>
                <li><a href="#features" className="hover:text-[#0F172A] transition-colors">Invoicing &amp; GST</a></li>
                <li><a href="#features" className="hover:text-[#0F172A] transition-colors">Job Cards</a></li>
                <li><a href="#features" className="hover:text-[#0F172A] transition-colors">Recurring Vouchers</a></li>
                <li><a href="#features" className="hover:text-[#0F172A] transition-colors">Reports Suite</a></li>
                <li><a href="#features" className="hover:text-[#0F172A] transition-colors">Multi-Company</a></li>
              </ul>
            </div>

            {/* Company */}
            <div className="col-span-1 md:col-span-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5500] font-semibold mb-4">Company</div>
              <ul className="space-y-2 text-sm text-[#475569]">
                <li><a href="#about" className="hover:text-[#0F172A] transition-colors">About</a></li>
                <li><a href="#pricing" className="hover:text-[#0F172A] transition-colors">Plans</a></li>
                <li><a href="#workflow" className="hover:text-[#0F172A] transition-colors">How it works</a></li>
                <li><Link to="/contact" className="hover:text-[#0F172A] transition-colors">Contact</Link></li>
                <li><Link to="/contact?source=careers" className="hover:text-[#0F172A] transition-colors">Careers</Link></li>
                <li><Link to="/contact?source=partners" className="hover:text-[#0F172A] transition-colors">Partners</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div className="col-span-2 md:col-span-4" data-testid="footer-contact">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5500] font-semibold mb-4">Get in touch</div>
              <div className="space-y-3">
                <a
                  href="mailto:info@sresources.co"
                  data-testid="footer-email"
                  className="group flex items-start gap-3 p-3 rounded-lg border transition-all hover:border-[#FF5500] bg-white"
                  style={{ borderColor: "#E2E8F0" }}
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0"
                        style={{ background: "#FFEDE0" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF5500" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">Email</div>
                    <div className="text-sm text-[#0F172A] font-mono-data group-hover:text-[#FF5500] transition-colors break-all">
                      info@sresources.co
                    </div>
                  </div>
                </a>

                <a
                  href="tel:+918079989999"
                  data-testid="footer-phone"
                  className="group flex items-start gap-3 p-3 rounded-lg border transition-all hover:border-[#FF5500] bg-white"
                  style={{ borderColor: "#E2E8F0" }}
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0"
                        style={{ background: "#FFEDE0" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF5500" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">Phone</div>
                    <div className="text-sm text-[#0F172A] font-mono-data group-hover:text-[#FF5500] transition-colors">
                      +91 80799 89999
                    </div>
                  </div>
                </a>

                <div className="flex items-center gap-2 mt-2 text-[11px]">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border"
                          style={{ background: "#FFEDE0",
                                    borderColor: "rgba(255,85,0,0.30)",
                                    color: "#FF5500" }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF5500]"/>
                    Audit-ready
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border"
                          style={{ background: "#DCFCE7",
                                    borderColor: "rgba(16,185,129,0.30)",
                                    color: "#16A34A" }}>
                    <Shield size={10}/>
                    GST · VAT
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Legal bar ── */}
          <div className="mt-14 pt-6 border-t flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#64748B]"
                style={{ borderColor: "#E2E8F0" }}
                data-testid="footer-legal">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <span className="font-mono-data">© {new Date().getFullYear()} SERP · All rights reserved.</span>
              <Link to="/terms" className="hover:text-[#0F172A] transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-[#0F172A] transition-colors">Privacy Policy</Link>
              <Link to="/privacy" className="hover:text-[#0F172A] transition-colors">Cookie Policy</Link>
            </div>
            <div className="font-mono-data text-[#475569]">
              A Product of <span className="text-[#0F172A] font-semibold">Shanvi Resources</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
